import React, { useState, useContext, useEffect } from 'react';
import { AppBar, Toolbar, IconButton, Box, Avatar, Tooltip, Typography, Button, Dialog, DialogTitle, DialogContent, List, ListItem, ListItemButton, ListItemText, ListItemIcon, Breadcrumbs, useTheme, alpha } from '@mui/material';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import SettingsIcon from '@mui/icons-material/Settings';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import MenuIcon from '@mui/icons-material/Menu';
import { AuthContext } from '../../contexts/AuthContext';
import { StoreContext } from '../../contexts/StoreContext';
import ProfileMenu from './ProfileMenu';
import SettingsModal from './SettingsModal';
import ViewModeToggle from '../common/ViewModeToggle';
import { ThemeToggle } from '../common/ThemeToggle';

const STORES = [
    { id: 'loja1', name: 'São Bento do Sul' },
    { id: 'loja2', name: 'Joinville' },
];

const TopBar = ({ onMenuClick }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const { auth } = useContext(AuthContext);
    const { viewMode, setViewMode } = useContext(StoreContext);
    const roleId = auth?.roleId ? Number(auth.roleId) : null;
    const { logout } = useContext(AuthContext); // Re-extracting for clarity

    // --- Store Logic ---
    const currentStoreId = localStorage.getItem("vochica_store") || "loja1";
    const [storeId, setStoreId] = useState(currentStoreId);
    const [openStoreDialog, setOpenStoreDialog] = useState(false);

    const handleSwitchStore = () => setOpenStoreDialog(true);
    const handleSelectStore = (newStoreId) => {
        localStorage.setItem("vochica_store", newStoreId);
        setStoreId(newStoreId);
        setOpenStoreDialog(false);
        window.dispatchEvent(new Event("vochica:store-changed"));
        window.location.reload();
    };

    const currentStoreName = STORES.find(s => s.id === storeId)?.name || storeId;

    // --- Profile Menu ---
    const [anchorElProfile, setAnchorElProfile] = useState(null);
    const openProfile = Boolean(anchorElProfile);
    const handleProfileClick = (event) => setAnchorElProfile(event.currentTarget);
    const handleProfileClose = () => setAnchorElProfile(null);

    // --- Settings Modal ---
    const [openSettings, setOpenSettings] = useState(false);
    const handleSettingsOpen = () => setOpenSettings(true);
    const handleSettingsClose = () => setOpenSettings(false);

    // Auto-close ALL menus when route changes
    useEffect(() => {
        setOpenSettings(false);
        setAnchorElProfile(null);
        setOpenStoreDialog(false);
    }, [location.pathname]);

    const showSettings = roleId === 1 || roleId === 3;

    // Generate Breadcrumbs based on path
    const pathnames = location.pathname.split('/').filter(x => x);

    return (
        <>
            <AppBar
                position="sticky"
                color="default"
                elevation={0}
                sx={{
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.default',
                    zIndex: 1100,
                    height: 64,
                    width: '100%',
                    transition: 'all 0.3s ease'
                }}
            >
                <Toolbar sx={{ justifyContent: 'space-between', minHeight: '64px !important', px: { xs: 2, md: 3 } }}>

                    {/* LEFT: BREADCRUMBS & CONTEXT */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {onMenuClick && (
                            <IconButton
                                color="inherit"
                                edge="start"
                                onClick={onMenuClick}
                                sx={{ display: { md: 'none' }, mr: 1 }}
                            >
                                <MenuIcon />
                            </IconButton>
                        )}
                        <Breadcrumbs
                            aria-label="breadcrumb"
                            sx={{ display: { xs: 'none', sm: 'block' } }}
                        >
                            <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>Vó Chica</Typography>
                            {pathnames.length > 0 && (
                                <Typography sx={{ color: 'text.primary', fontSize: '0.875rem', fontWeight: 600, textTransform: 'capitalize' }}>
                                    {pathnames[pathnames.length - 1].replace(/-/g, ' ')}
                                </Typography>
                            )}
                        </Breadcrumbs>
                    </Box>

                    {/* RIGHT: STORE, ACTIONS, PROFILE */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>

                        {/* Store Selector (only for users with unrestricted access) */}
                        {(!auth?.user?.loja_vinculada || auth.user.loja_vinculada === 'todas') && (
                            <Button
                                onClick={handleSwitchStore}
                                sx={{
                                    textTransform: 'none',
                                    color: theme.palette.text.primary,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    px: 1.5,
                                    py: 0.5,
                                    borderRadius: 100,
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                    bgcolor: 'transparent',
                                    '&:hover': {
                                        bgcolor: 'rgba(255, 255, 255, 0.05)',
                                        borderColor: theme.palette.primary.main,
                                        color: theme.palette.primary.main
                                    }
                                }}
                            >
                                <StorefrontIcon sx={{ fontSize: 18, color: theme.palette.semantic.actionRed }} />
                                <Typography variant="body2" fontWeight={600} sx={{ display: { xs: 'none', sm: 'block' } }}>
                                    {currentStoreName}
                                </Typography>
                                <ExpandMoreIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                            </Button>
                        )}

                        <Box sx={{ height: 20, width: '1px', bgcolor: theme.palette.divider, mx: 0.5 }} />

                        {/* Theme Toggle Button */}
                        <ThemeToggle />

                        {/* View Mode Toggle */}
                        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                            <ViewModeToggle value={viewMode} onChange={setViewMode} />
                        </Box>

                        {/* Actions */}
                        {showSettings && (
                            <Tooltip title="Configurações">
                                <IconButton 
                                    onClick={handleSettingsOpen} 
                                    size="small" 
                                    sx={{ color: 'text.secondary', display: { xs: 'none', sm: 'flex' } }}
                                >
                                    <SettingsIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}

                        {/* Profile Avatar */}
                        <IconButton
                            onClick={handleProfileClick}
                            size="small"
                            sx={{
                                p: 0.5,
                                border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                transition: 'all 0.2s',
                                '&:hover': { borderColor: theme.palette.primary.main }
                            }}
                        >
                            <Avatar
                                alt={auth.user?.name || "Usuário"}
                                src={auth.user?.photo_url}
                                sx={{
                                    width: 32,
                                    height: 32,
                                    bgcolor: theme.palette.semantic.actionRed,
                                    fontSize: 14,
                                    fontWeight: 700
                                }}
                            >
                                {auth.user?.name ? auth.user.name[0] : 'U'}
                            </Avatar>
                        </IconButton>
                    </Box>
                </Toolbar>
            </AppBar>

            {/* Components */}
            <ProfileMenu anchorEl={anchorElProfile} open={openProfile} onClose={handleProfileClose} />
            <SettingsModal key={location.pathname} open={openSettings} onClose={handleSettingsClose} />

            <Dialog open={openStoreDialog} onClose={() => setOpenStoreDialog(false)} PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ fontWeight: 700 }}>Selecionar Loja</DialogTitle>
                <DialogContent>
                    <List sx={{ pt: 0 }}>
                        {STORES.map((store) => (
                            <ListItem disableGutters key={store.id}>
                                <ListItemButton onClick={() => handleSelectStore(store.id)} selected={storeId === store.id} sx={{ borderRadius: 2 }}>
                                    <ListItemIcon>
                                        <LocationOnIcon sx={{ color: storeId === store.id ? theme.palette.semantic.actionRed : theme.palette.text.disabled }} />
                                    </ListItemIcon>
                                    <ListItemText primary={store.name} primaryTypographyProps={{ fontWeight: storeId === store.id ? 700 : 400 }} />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default TopBar;
